import { MoneyDisplay } from '@/components/ui/MoneyDisplay';

interface ChartTooltipPayload {
    periodLabel: string;
    value: number | null;
    isCarriedForward: boolean;
    previousValue?: number | null;
    valueChange?: number | null;
    growthPercentage?: number | null;
}

interface ChartTooltipProps {
    /** Whether the tooltip is active (from Recharts) */
    active?: boolean;
    /** Payload data from Recharts */
    payload?: ReadonlyArray<{ payload?: ChartTooltipPayload }>;
    /** Display currency for the values */
    currency: string;
    /** Whether this is a debt ledger (affects labels and color logic) */
    isDebt: boolean;
}

/**
 * Format a percentage value for display.
 */
function formatPercentage(value: number | null | undefined): string {
    if (value === null || value === undefined) {
        return 'N/A';
    }
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
}

/**
 * Custom tooltip component for the historical value chart.
 * Displays the period label, formatted value, value change, growth percentage,
 * and carried forward indicator.
 *
 * For assets: positive change = green (good), negative = red (bad)
 * For debt: positive change = red (bad, debt increased), negative = green (good, debt decreased)
 *
 * This component is used as the `content` prop of Recharts' Tooltip component.
 */
export function ChartTooltip({ active, payload, currency, isDebt }: ChartTooltipProps) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    const data = payload[0]?.payload;
    if (!data) {
        return null;
    }

    const { periodLabel, value, isCarriedForward, valueChange, growthPercentage } = data;

    // Determine if change is positive (for color logic)
    const isPositiveChange = valueChange !== null && valueChange !== undefined && valueChange > 0;

    // For assets: positive = good (green), negative = bad (red)
    // For debt: positive = bad (red, debt increased), negative = good (green, debt decreased)
    const isGoodChange = isDebt ? !isPositiveChange : isPositiveChange;

    // Color classes based on whether the change is "good" or "bad"
    const changeColorClass = valueChange === 0
        ? 'text-gray-500 dark:text-gray-400'
        : isGoodChange
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';

    // Arrow indicator
    const arrow = valueChange !== null && valueChange !== undefined
        ? valueChange > 0 ? '↑' : valueChange < 0 ? '↓' : '→'
        : null;

    // Labels based on ledger type
    const changeLabel = isDebt ? 'Debt change' : 'Value change';
    const percentLabel = isDebt ? 'Debt %' : 'Growth %';

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary shadow-lg p-2 sm:p-3 sm:min-w-[160px]">
            {/* Period label */}
            <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1">
                {periodLabel}
            </p>

            {value !== null ? (
                <>
                    {/* Desktop: labeled rows */}
                    <div className="hidden sm:block">
                        <div className="flex items-center gap-2 mb-2">
                            <MoneyDisplay
                                value={value}
                                currency={currency}
                                size="md"
                                className="font-semibold text-gray-900 dark:text-white"
                            />
                        </div>
                        {valueChange !== null && valueChange !== undefined && (
                            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-1">
                                <div className="flex justify-between items-center gap-3 text-xs">
                                    <span className="text-gray-500 dark:text-gray-400">{changeLabel}:</span>
                                    <span className={`font-medium ${changeColorClass}`}>
                                        {arrow}{' '}
                                        <MoneyDisplay
                                            value={Math.abs(valueChange)}
                                            currency={currency}
                                            size="sm"
                                            className="inline"
                                        />
                                    </span>
                                </div>
                                <div className="flex justify-between items-center gap-3 text-xs">
                                    <span className="text-gray-500 dark:text-gray-400">{percentLabel}:</span>
                                    <span className={`font-medium ${changeColorClass}`}>
                                        {formatPercentage(growthPercentage)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Mobile: vertical stacked layout with labels */}
                    <div className="sm:hidden space-y-1">
                        <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Value</div>
                            <MoneyDisplay
                                value={value}
                                currency={currency}
                                size="sm"
                                className="font-semibold text-gray-900 dark:text-white"
                            />
                        </div>
                        {valueChange !== null && valueChange !== undefined && (
                            <>
                                <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{changeLabel}</div>
                                    <div className={`text-xs font-medium ${changeColorClass}`}>
                                        {arrow}{' '}
                                        <MoneyDisplay
                                            value={Math.abs(valueChange)}
                                            currency={currency}
                                            size="sm"
                                            className="inline"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{percentLabel}</div>
                                    <div className={`text-xs font-medium ${changeColorClass}`}>
                                        {formatPercentage(growthPercentage)}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </>
            ) : (
                <span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">
                    No data available
                </span>
            )}

            {isCarriedForward && (
                <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                    Carried forward
                </p>
            )}
        </div>
    );
}
